/* eslint-disable max-lines-per-function */
import React, { useState, useRef, useEffect, useContext } from 'react';
import {
  Tooltip,
  Collapse,
  Typography,
  IconButton,
  Card,
  CardContent,
  Grid,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import './employeeAchievementDetails.scss';
import ViewAward from './EmployeeAwardModals/employeeViewAward';
import { SKILL_CERT_PROFILE } from '../../../Constants/strings';
import DeleteIcon from '../../../Assets/images/svg/dar_delete.svg';
import { AuthContext } from '../../../Contexts/AuthContext';
import DeleteAward from './EmployeeAwardModals/employeeDeleteAward';
import NoEmployeeDetails from './noEmployeeDetails';
import AddAward from './EmployeeAwardModals/employeeAddAward';
import PropTypes from 'prop-types';
import * as employeeAchievementUtils from './employeeAchievementDetailsUtil';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import { useLocation } from 'react-router-dom';
import { MODULE_NAMES } from '../../../Constants/modules';
import DriDetailCard from './driDetailCard';
import SkillsAwardSection from './skillsAwardSection';

const SkillsCertAward = ({ userId }) => {
  const [certificationsExpanded, setCertificationsExpanded] = useState(false);
  const [skillsExpanded, setSkillsExpanded] = useState(false);
  const [coreSkillsExpanded, setCoreSkillsExpanded] = useState(false);
  const [nonCoreSkillsExpanded, setNonCoreSkillsExpanded] = useState(false);
  const [awardsExpanded, setAwardsExpanded] = useState(false);
  const [driExpanded, setDriExpanded] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedAward, setSelectedAward] = useState([]);
  const [showCertLeftArrow, setShowCertLeftArrow] = useState(false);
  const [showCertRightArrow, setShowCertRightArrow] = useState(false);
  const [showAwardsLeftArrow, setShowAwardsLeftArrow] = useState(false);
  const [showAwardsRightArrow, setShowAwardsRightArrow] = useState(false);
  const [skills, setSkills] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [awards, setAwards] = useState([]);
  const [dri, setDri] = useState([]);
  const location = useLocation();
  const { emailId } = location.state || {};

  const { snackbarOpen } = useSnackbar();
  const carouselRefCertifications = useRef(null);
  const carouselRefAwards = useRef(null);
  const toggleCertifications = () =>
    setCertificationsExpanded(!certificationsExpanded);
  const toggleSkills = () => setSkillsExpanded(!skillsExpanded);
  const toggleCoreSkills = () => setCoreSkillsExpanded(!coreSkillsExpanded);
  const toggleNonCoreSkills = () =>
    setNonCoreSkillsExpanded(!nonCoreSkillsExpanded);
  const toggleAwards = () => setAwardsExpanded(!awardsExpanded);
  const toggleDri = () => setDriExpanded(!driExpanded);
  const { userProfile } = useContext(AuthContext);
  const permission = userProfile?.permissions
    ? userProfile.permissions[MODULE_NAMES.EMPLOYEE_ACHIEVEMENTS] || []
    : [];
  const hasAccess = (action) => {
    return permission.includes(action);
  };

  useEffect(() => {
    fetchApiData();
  }, [userId]);
  const fetchApiData = async () => {
    try {
      const skillsData
        = await employeeAchievementUtils.fetchMappedSkills(userId);
      setSkills(skillsData);
      const certificationData
        = await employeeAchievementUtils.fetchMapCertifications(userId);
      setCertifications(certificationData);
      const awardData = await employeeAchievementUtils.fetchAwards(userId); // Await the fetchAwards function
      setAwards(awardData);
      const driData = await employeeAchievementUtils.fetchDri(emailId);
      setDri(driData);
    } catch (error) {
      snackbarOpen(SKILL_CERT_PROFILE.OOPS_TRY_AGAIN, 'warning');
      console.error('Error fetching data:', error);
    }
  };

  const checkArrows = (carouselRef, setShowRightArrow, setShowLeftArrow) => {
    const carousel = carouselRef.current;
    if (carousel) {
      setShowRightArrow(carousel.scrollWidth > carousel.clientWidth);
      setShowLeftArrow(carousel.scrollLeft > 0);
    }
  };
  useEffect(() => {
    checkArrows(
      carouselRefCertifications,
      setShowCertRightArrow,
      setShowCertLeftArrow,
    );
    checkArrows(
      carouselRefAwards,
      setShowAwardsRightArrow,
      setShowAwardsLeftArrow,
    );
  }, [certifications, awards]); // Update if certifications or awards change

  const handleScroll = (
    carouselRef,
    setShowRightArrow,
    setShowLeftArrow,
    direction,
  ) => {
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.scrollBy({ left: direction * 200, behavior: 'smooth' });
      // Update arrow visibility after scrolling
      setTimeout(() => {
        setShowRightArrow(
          carousel.scrollLeft < carousel.scrollWidth - carousel.clientWidth,
        );
        setShowLeftArrow(carousel.scrollLeft > 0);
      }, 100); // Adjust timeout based on your scroll behavior
    }
  };

  const handleViewAward = (award) => {
    setViewModalOpen(true);
    setSelectedAward(award);
  };

  const handleDeleteAward = (award) => () => {
    setDeleteModalOpen(true);
    setSelectedAward(award);
  };

  const renderCertificationCard = (cert) => {
    return (
      <div key={cert.id} className="cert-carousel-item">
        <Card className="certification-card">
          <CardContent>
            <Typography className="profile-cert-id">
              {cert.certification_code}
              {cert.certification_type === 'Professional' && (
                <span>{SKILL_CERT_PROFILE.PROFESSIONAL}</span>
              )}
            </Typography>
            <Tooltip
              title={cert.name}
              classes={{ tooltip: 'map-role-btn-tooltip' }}
              disableHoverListener={
                cert.certifications.length < 30
              }
            >
              <Tooltip>
                <div className="profile-cert-name scooped-corners back">
                  <Typography className="profile-cert-name-text scooped-corners">
                    {cert.certifications.length > 30
                      ? `${cert.certifications.substring(
                        0,
                        30,
                      )}...`
                      : cert.certifications}
                  </Typography>
                </div>
              </Tooltip>
            </Tooltip>
            <Typography>
              {SKILL_CERT_PROFILE.ACHIEVED_DATE}
              &nbsp;&nbsp;&nbsp;
              <span className="cert-date">
                :
                {employeeAchievementUtils.formatDate(
                  cert.achievedDate,
                )}
              </span>
            </Typography>
            <Typography>
              {SKILL_CERT_PROFILE.EXPIRY_DATE}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <span className="cert-date">
                :
                {employeeAchievementUtils.formatDate(
                  cert.expiredDate,
                )}
              </span>
            </Typography>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderDriDetails = (driDetails) => {
    return driDetails.map((score) => (
      <Grid className="profile-dri" item key={score.driId}>
        <DriDetailCard key={score.driId} score={score}/>
      </Grid>
    ));
  };

  const renderCoreSkills = (coreSkills) => {
    return coreSkills?.skillMapDetails
      ?.filter((skill) => skill.isCoreSkill === 1)
      .map((skill) => (
        <div
          key={skill?.skillMapId}
          className="profile-skill-info"
        >
          <div
            className={`skill-level ${
              skill.skillLevel
                ? `level-${skill?.skillLevel}`
                : ''
            }`}
          >
            {`L${skill.skillLevel}`}
          </div>
          <Typography className="skill-name">
            {skill.skillName}
          </Typography>
        </div>
      ));
  };

  return (
    <div className="skill-cert-award-component">
      {/* Certifications */}
      <div className="section">
        <SkillsAwardSection
          sectionClass="skills-award-section-title"
          SKILL_CERT_PROFILE={SKILL_CERT_PROFILE}
          certifications={certifications}
          certificationsExpanded={certificationsExpanded}
          toggleCertifications={toggleCertifications}
        />
        <Collapse className="skills-award-line" in={certificationsExpanded}>
          <div className="outer-div">
            <div className="cert-carousel-container">
              {certifications?.certificationMapDetails?.length > 0 ? (
                <>
                  {showCertLeftArrow && (
                    <div className="carousel-nav-prev-div">
                      <IconButton
                        className="carousel-nav prev"
                        onClick={() =>
                          handleScroll(
                            carouselRefCertifications,
                            setShowCertRightArrow,
                            setShowCertLeftArrow,
                            -1,
                          )
                        }
                      >
                        <KeyboardDoubleArrowLeftIcon />
                      </IconButton>
                    </div>
                  )}
                  <div
                    ref={carouselRefCertifications}
                    className="cert-carousel"
                  >
                    {certifications?.certificationMapDetails?.map((cert) => renderCertificationCard(cert))}
                  </div>
                  {showCertRightArrow && (
                    <div className="carousel-nav-next-div">
                      <IconButton
                        className="carousel-nav next"
                        onClick={() =>
                          handleScroll(
                            carouselRefCertifications,
                            setShowCertRightArrow,
                            setShowCertLeftArrow,
                            1,
                          )
                        }
                      >
                        <KeyboardDoubleArrowRightIcon />
                      </IconButton>
                    </div>
                  )}
                </>
              ) : (
                <NoEmployeeDetails message={SKILL_CERT_PROFILE.NO_DATA}/>
              )}
            </div>
          </div>
        </Collapse>
      </div>
      {/* Skills */}
      <div className="section">
        <Typography className="skills-award-section-title">
          <span className="skills-award-title">
            {SKILL_CERT_PROFILE.SKILLS}
          </span>
          <span className="skills-award-count-text">
            {SKILL_CERT_PROFILE.TOTAL_SKILL_COUNT}:{' '}
            <span>{skills?.totalCount || 0}</span>
            <IconButton onClick={toggleSkills}>
              {skillsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </span>
        </Typography>
        <Collapse in={skillsExpanded}>
          <div className="outer-div">
            <div className="subsection scrollable">
              <Typography className="skills-award-sub-section-title">
                <span>{SKILL_CERT_PROFILE.CORE}</span>
                <span className="skills-award-count-text">
                  {SKILL_CERT_PROFILE.TOTAL}:{' '}
                  <span>{skills?.coreSkillCount || 0}</span>
                  <IconButton onClick={toggleCoreSkills}>
                    {coreSkillsExpanded ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </span>
              </Typography>
              <Collapse in={coreSkillsExpanded}>
                <div className="subsection-outer-div">
                  {skills?.skillMapDetails?.filter(
                    (skill) => skill.isCoreSkill === 1,
                  ).length > 0 ? (
                      <div className="skills-list">
                        {renderCoreSkills(skills)}
                      </div>
                    ) : (
                      <NoEmployeeDetails message={SKILL_CERT_PROFILE.NO_DATA} noEmployeeCss="no-data-css" />
                    )}
                </div>
              </Collapse>
            </div>
            <div className="subsection scrollable">
              <Typography className="skills-award-sub-section-title">
                <span>{SKILL_CERT_PROFILE.NON_CORE}</span>
                <span className="skills-award-count-text">
                  {SKILL_CERT_PROFILE.TOTAL}:{' '}
                  <span>{skills?.nonCoreSkillCount || 0}</span>
                  <IconButton onClick={toggleNonCoreSkills}>
                    {nonCoreSkillsExpanded ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </span>
              </Typography>
              <Collapse in={nonCoreSkillsExpanded}>
                <div className="subsection-outer-div">
                  {skills?.skillMapDetails?.filter(
                    (skill) => skill.isCoreSkill === 0,
                  ).length > 0 ? (
                      <div className="skills-list">
                        {skills?.skillMapDetails
                          ?.filter((skill) => skill.isCoreSkill === 0)
                          .map((skill) => (
                            <div
                              key={skill?.skillMapId}
                              className="profile-skill-info"
                            >
                              <div
                                className={`skill-level ${
                                  skill.skillLevel
                                    ? `level-${skill?.skillLevel}`
                                    : ''
                                }`}
                              >
                                {`L${skill.skillLevel}`}
                              </div>
                              <Typography className="skill-name">
                                {skill.skillName}
                              </Typography>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <NoEmployeeDetails message={SKILL_CERT_PROFILE.NO_DATA} noEmployeeCss="no-data-css" />
                    )}
                </div>
              </Collapse>
            </div>
          </div>
        </Collapse>
      </div>
      {/* Awards */}
      <div className="section">
        <Typography className="skills-award-section-title">
          <span className="skills-award-title">
            {SKILL_CERT_PROFILE.AWARDS}
            {hasAccess('create') && (
              <IconButton onClick={() => setAddModalOpen(true)}>
                <AddIcon />
              </IconButton>
            )}
          </span>
          <span className="skills-award-count-text">
            {SKILL_CERT_PROFILE.TOTAL_AWARD_COUNT}:{' '}
            <span>{awards?.awardCount || 0}</span>
            <IconButton onClick={toggleAwards}>
              {awardsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </span>
        </Typography>
        <Collapse in={awardsExpanded}>
          <div className="outer-div">
            <div className="award-carousel-container">
              {awards?.awards?.length > 0 ? (
                <>
                  {showAwardsLeftArrow && (
                    <div className="carousel-nav-prev-div">
                      <IconButton
                        className="carousel-nav prev"
                        onClick={() =>
                          handleScroll(
                            carouselRefAwards,
                            setShowAwardsRightArrow,
                            setShowAwardsLeftArrow,
                            -1,
                          )
                        }
                      >
                        <KeyboardDoubleArrowLeftIcon />
                      </IconButton>
                    </div>
                  )}
                  <div ref={carouselRefAwards} className="award-carousel">
                    {awards?.awards?.map((award) => (
                      <div key={award.award_id} className="award-carousel-item">
                        <Card>
                          <Typography className="award-name">
                            {award.award_name.length > 20
                              ? `${award.award_name.slice(0, 20)}...`
                              : award.award_name}
                          </Typography>
                          <Typography className="award-year">
                            {award.year}
                          </Typography>
                          <div className="award-view-btn-div">
                            <Button
                              className="award-view-btn"
                              variant="outlined"
                              onClick={() => handleViewAward(award)}
                            >
                              {SKILL_CERT_PROFILE.VIEW}
                            </Button>
                          </div>
                        </Card>
                        {hasAccess('delete') && (
                          <IconButton
                            className="award-delete-icon"
                            onClick={handleDeleteAward(award)}
                          >
                            <img src={DeleteIcon} alt="Delete" />
                          </IconButton>
                        )}
                      </div>
                    ))}
                  </div>
                  {showAwardsRightArrow && (
                    <div className="carousel-nav-next-div">
                      <IconButton
                        className="carousel-nav next"
                        onClick={() =>
                          handleScroll(
                            carouselRefAwards,
                            setShowAwardsRightArrow,
                            setShowAwardsLeftArrow,
                            1,
                          )
                        }
                      >
                        <KeyboardDoubleArrowRightIcon />
                      </IconButton>
                    </div>
                  )}
                </>
              ) : (
                <NoEmployeeDetails message={SKILL_CERT_PROFILE.NO_DATA} />
              )}
            </div>
          </div>
        </Collapse>
      </div>
      {/* DRI Scores */}
      <div className="section">
        <Typography className="skills-award-section-title">
          <span className="skills-award-title">{SKILL_CERT_PROFILE.DRI}</span>
          <span className="skills-award-count-text">
            {SKILL_CERT_PROFILE.TOTAL_DRI_SCORE}:{' '}
            <span>{dri?.driDetails?.length || 0}</span>
            <IconButton onClick={toggleDri}>
              {driExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </span>
        </Typography>
        <Collapse in={driExpanded}>
          <div className="profile-dri-outer-div">
            {!dri?.driDetails?.length > 0 ? (
              <NoEmployeeDetails message={SKILL_CERT_PROFILE.NO_DATA}/>
            ) : (
              <Grid container width="100%" spacing={2} m={1}>
                {renderDriDetails(dri.driDetails)}
              </Grid>
            )}
          </div>
        </Collapse>
      </div>
      <ViewAward // viewAward popup
        modalOpen={viewModalOpen}
        handleClose={() => setViewModalOpen(false)}
        selectedAward={selectedAward}
      />
      <DeleteAward // deleteAward popup
        modalOpen={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        selectedAward={selectedAward}
        updatedData={fetchApiData}
      />
      <AddAward // AddAward popup
        modalOpen={addModalOpen}
        handleClose={() => setAddModalOpen(false)}
        userId={userId}
        updatedData={fetchApiData}
      />
    </div>
  );
};
SkillsCertAward.propTypes = {
  userId: PropTypes.string.isRequired,
};

export default SkillsCertAward;
